import { RegularButton } from "@components/button"
import { BasicModal, LoginModal } from "@components/modal"
import VideoPlayer from "@components/video/VideoPlayer"
import { Container, FlexBox } from "@components/wrapper"
import { HARISENIN_PUBLIC_ICON } from "@constants/const/pictures"
import { flex_center } from "@constants/styles"
import { CourseDetail, OrderCheckType } from "@interfaces/course/course-detail"
// import { AnalyticsProvider, useAnalytics } from "@lib/context/AnalyticsContext"
import { useUserData } from "@lib/context/ComponentContext"
import { priceFormatter } from "@lib/functions"
import { CheckoutSummary } from "@modules/checkout/types/checkout"
import CourseDetailServices from "@services/course-detail.services"
import clsx from "clsx"
import Image from "next/image"
import { useRouter } from "next/router"
import process from "process"
import { FC, useEffect, useState } from "react"
import { AiOutlineInfoCircle } from "react-icons/ai"
import { FaTimes } from "react-icons/fa"
import { IoPlay } from "react-icons/io5"
import { CourseShare } from "./CourseShare"
import { ModalDigitalPlatform } from "./ModalDigitalPlatform"

interface ClassSidebarProps {
  isSidebarChanged: boolean
  isFixed: boolean
  screenWidth: number
  classDetail: CourseDetail
}

const InfoRow = ({ icon, label }: { icon: string; label: string }) => {
  return (
    <FlexBox align="center" className="text-sm font-semibold gap-2">
      <FlexBox className="w-4.5" align="center">
        <Image
          src={`${HARISENIN_PUBLIC_ICON}/${icon}`}
          width="14"
          height={14}
          alt={icon ?? "course icon"}
        />
      </FlexBox>
      <div>{label}</div>
    </FlexBox>
  )
}

export const CourseSidebar: FC<ClassSidebarProps> = ({
  isSidebarChanged,
  isFixed,
  screenWidth,
  classDetail,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showVideo, setShowVideo] = useState(false)

  const [buttonLabel, setButtonLabel] = useState("Beli Sekarang")
  const [buttonStatus, setButtonStatus] = useState<OrderCheckType>("NOT_YET")
  const [isChecking, setIsChecking] = useState(true)

  const [isModalPlatformOpen, setIsModalPlatformOpen] = useState(false)

  const { isLogin } = useUserData()
  const router = useRouter()
  // const { analytics } = useAnalytics()
  const { course_info: info, id } = classDetail

  const course = new CourseDetailServices()

  useEffect(() => {
    const checkOrder = async () => {
      try {
        const res = await course.checkOrder(id)

        if (res.isSuccess) {
          const result = res.getValue()

          switch (result.status) {
            case "SUBMITTED_PAYMENT":
              setButtonLabel("Ke Pembayaran")
              break
            case "SETTLED":
              setButtonLabel("Ke Kelas")
              break
            default:
              setButtonLabel("Beli Sekarang")
              break
          }

          setButtonStatus(result.status)
        } else {
          setButtonLabel("Beli Sekarang")
        }

        setIsChecking(false)
      } catch (e) {
        setButtonLabel("Beli Sekarang")
        setIsChecking(false)
      }
    }

    if (isLogin) {
      checkOrder()
    } else {
      setIsChecking(false)
    }
  }, [isLogin])

  const INFOS = [
    {
      icon: "icon_test.svg",
      label: "Ujian Akhir",
    },
    {
      icon: "icon_pretest.svg",
      label: "Pretest",
    },
    // {
    //   icon: "icon_minute.svg",
    //   label: `${info.total_minutes} Menit Materi`,
    // },
    {
      icon: "icon_document.svg",
      label: `${info.total_documents} Dokumen`,
    },
    {
      icon: "icon_video.svg",
      label: `${info.total_video} Video`,
    },
    {
      icon: "icon_discussion.svg",
      label: "Diskusi",
    },
    {
      icon: "icon_certificate.svg",
      label: "Sertifikat",
    },
  ]

  async function handleCheckout() {
    if (!isLogin) {
      setIsModalOpen(true)
    } else {
      const checkoutData: CheckoutSummary = {
        class_id: classDetail.id,
        class_sku: classDetail.course_sku,
        class_name: classDetail.course_name,
        class_slug: classDetail.course_slug,
        class_picture: classDetail.course_banner.banner_image_path,
        class_category: classDetail.course_categories[0].category_name,
        class_brand: "HSL",
        class_variant: classDetail.course_label,
        class_image_url: classDetail.course_thumbnail.course_thumbnail_path,
        class_url: `${process.env.HOME_URL}/learning/kelas/${classDetail.course_slug}`,
        class_price: classDetail.course_price,
        total_document: info.total_documents,
        total_minutes: info.total_minutes,
        total_video: info.total_video,
        total_price: info.nett_price,
      }

      switch (buttonStatus) {
        case "NOT_YET": {
          // analytics.track("Product Clicked", {
          //   products: [
          //     {
          //       product_id: classDetail.course_sku,
          //       name: classDetail.course_name,
          //       affiliation: "PT. Karya Kaya Bahagia",
          //       coupon: "",
          //       discount: 0,
          //       currency: "IDR",
          //       brand: "HSL",
          //       category: classDetail.course_categories[0].category_name,
          //       variant: "",
          //       price: classDetail.course_price,
          //       quantity: 1,
          //       position: 0,
          //       item_list_name: classDetail.course_name,
          //       item_list_id: classDetail.id,
          //     },
          //   ],
          //   item_list_name: classDetail.course_name,
          //   item_list_id: classDetail.id,
          // })

          window.localStorage.setItem("hsl_checkout", JSON.stringify(checkoutData))
          window.location.href = "/learning/checkout"
          break
        }
        case "SUBMITTED_PAYMENT": {
          window.location.href = "/dashboard/order?type=learning"
          break
        }
        case "SETTLED": {
          await router.push(`/dashboard/kelas/${classDetail.id}/pretest`)
          break
        }
      }
    }
  }

  function handleOpenPlatform() {
    if (!isLogin) {
      return setIsModalOpen(true)
    }

    setIsModalPlatformOpen(true)
  }

  function handleCloseModal() {
    setIsModalOpen(false)
  }

  function handleOpenVideo() {
    setShowVideo(true)
  }

  function handleCloseVideo() {
    setShowVideo(false)
  }

  return (
    // <AnalyticsProvider>
    <>
      {/* Sticky mobile button */}
      {screenWidth < 576 && (
        <Container className="fixed bottom-0 bg-primary-grey py-3 z-[12]">
          <FlexBox className="gap-2.5" direction="col">
            <div className="text-xl font-semibold">{priceFormatter(info.nett_price)}</div>
            <FlexBox className="gap-2">
              {buttonLabel === "Beli Sekarang" && (
                <RegularButton
                  className={clsx(classDetail.course_label === "PRAKERJA" ? "w-full" : "w-1/2")}
                  variant="transparent"
                  onClick={handleOpenPlatform}
                  customPadding="px-1 py-2"
                >
                  Bayar via Platform Digital
                </RegularButton>
              )}

              {(classDetail.course_label === "NON_PRAKERJA" || buttonLabel === "Ke Kelas") && (
                <RegularButton
                  className={clsx(buttonLabel === "Beli Sekarang" ? "w-1/2" : "w-full")}
                  onClick={handleCheckout}
                  isSubmitting={isChecking}
                >
                  {buttonLabel ?? "Beli Sekarang"}
                </RegularButton>
              )}
            </FlexBox>
          </FlexBox>
        </Container>
      )}

      <section
        className={clsx(
          "sm:right-[calc(5vw+15px)] z-10 bg-white sm:w-1/4 rounded shadow-lg sm:mx-0 sm:mt-0",
          isFixed ? "sm:fixed top-[80px]" : "sm:absolute bottom-0",
          "w-[calc(100%-30px)] mx-[15px] mt-5",
          "sm-only:p-5"
        )}
      >
        {/* Video Thumbnail */}
        <div
          className={clsx(
            "sm:p-2 relative transition-[height]",
            isSidebarChanged ? "sm:h-0" : "sm:h-[14vw]",
            "sm:h-[54vw h-[]"
          )}
        >
          {!isSidebarChanged && (
            <>
              <Image
                src={classDetail.course_thumbnail.course_thumbnail_path}
                alt={classDetail.course_slug}
                className="w-full h-full object-cover sm-only:rounded"
                width={1280}
                height={720}
              />

              <FlexBox
                justify="center"
                align="center"
                className="absolute top-0 left-0 w-full h-full bg-overlay cursor-pointer"
                onClick={handleOpenVideo}
              >
                <div className={clsx("rounded-full bg-white", "w-[50px] h-[50px]", flex_center)}>
                  <IoPlay className="sm:text-xl" />
                </div>
              </FlexBox>
            </>
          )}
        </div>

        <div className={clsx("sm:px-[30px] pt-5 sm:pb-5", "")}>
          {/* Price & Order Button (Web Only) */}
          {screenWidth > 576 && (
            <>
              <div className="text-[32px] font-semibold text-[#023C17]">
                {priceFormatter(info.nett_price)}
              </div>

              <FlexBox className="gap-2 mb-6">
                {info.discount_percentage > 0 && (
                  <div className="bg-red text-white text-sm w-fit rounded px-1.5 py-1 font-semibold">
                    -{info.discount_percentage}%
                  </div>
                )}

                {info.discount_price > 0 && (
                  <div className="text-grey-97 line-through text-lg font-semibold">
                    {priceFormatter(info.discount_price)}
                  </div>
                )}
              </FlexBox>

              <FlexBox align="center" className="text-blue text-sm mb-4">
                {info.offer && (
                  <>
                    <AiOutlineInfoCircle className="text-lg" />
                    <div className="ml-3">{info.offer}</div>
                  </>
                )}
              </FlexBox>

              <FlexBox direction="col" className="gap-4 mb-8">
                {/* {(classDetail.course_label === "NON_PRAKERJA" || buttonLabel === "Ke Kelas") && ( */}
                <FlexBox className="gap-3">
                  <RegularButton
                    className="w-full"
                    onClick={handleCheckout}
                    isSubmitting={isChecking}
                    customPadding="py-3"
                    customTextSize="text-sm"
                  >
                    {buttonLabel ?? "Beli Sekarang"}
                  </RegularButton>
                  <CourseShare screenWidth={screenWidth} slug={classDetail.course_slug} />
                </FlexBox>
                {/* // )} */}

                {buttonLabel === "Beli Sekarang" && (
                  <RegularButton
                    className="w-full"
                    customPadding="py-3"
                    customTextSize="text-sm"
                    variant="transparent"
                    onClick={handleOpenPlatform}
                  >
                    Bayar via Platform Digital
                  </RegularButton>
                )}
              </FlexBox>
            </>
          )}

          {/* Info column */}
          <div>
            <div className=" font-semibold mb-4">Kelas ini sudah termasuk</div>

            <div className="gap-3 mb-5 grid grid-cols-2">
              {INFOS.map((i, index) => (
                <InfoRow icon={i.icon} label={i.label} key={index} />
              ))}
            </div>

            <div className=" font-semibold mb-4">Bahasa Pengantar</div>
            <div className="font-semibold text-sm">Bahasa Indonesia</div>
          </div>
        </div>
      </section>

      <LoginModal show={isModalOpen} onClose={handleCloseModal} />

      {/* Video Player */}
      <BasicModal show={showVideo} onClose={handleCloseVideo}>
        <div className="relative">
          <button
            className="absolute right-[3%] sm:top-[5%] top-[8%] z-[1000]"
            onClick={handleCloseVideo}
          >
            <FaTimes className="sm:text-2xl text-white text-lg" />
          </button>

          <VideoPlayer
            url={info.introduction_video_link}
            videoTitle="Introduction Video"
            className="w-[80vw] h-[45vw]"
            thumbnail={classDetail.course_thumbnail.course_thumbnail_path}
          />
        </div>
      </BasicModal>

      <ModalDigitalPlatform
        show={isModalPlatformOpen}
        onClose={() => setIsModalPlatformOpen(false)}
        platforms={classDetail.course_platform_digital ?? []}
        courseId={classDetail.id}
      />
    </>
    // </AnalyticsProvider>
  )
}
